import React from 'react';
import type { Agent, AgentStatus, Tier } from '../types';
import { TierNames, StatusColors } from '../types';
import { formatPercent, formatRam, formatUptime, formatDid } from '../utils/format';

export interface AgentFleetProps {
  agents: Agent[];
  className?: string;
  onSpawn?: () => void;
  onSelect?: (agent: Agent) => void;
  onPause?: (agent: Agent) => void;
  onTerminate?: (agent: Agent) => void;
  onUpgrade?: (agent: Agent) => void;
  onViewLogs?: (agent: Agent) => void;
}

/**
 * Agent Fleet Management Panel
 * View and control all Clawbots
 */
export function AgentFleet({
  agents,
  className = '',
  onSpawn,
  onSelect,
  onPause,
  onTerminate,
  onUpgrade,
  onViewLogs,
}: AgentFleetProps) {
  const activeAgents = agents.filter((a) => a.status === 'active');
  const sandboxAgents = agents.filter((a) => a.status === 'sandbox');

  return (
    <div className={`bg-gray-900 border border-gray-800 rounded-lg p-6 ${className}`}>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-lg font-semibold text-white">Agent Fleet</h2>
        <div className="flex items-center gap-4">
          <div className="text-sm text-gray-400">
            <span className="text-white font-semibold">{activeAgents.length}</span> active
            {sandboxAgents.length > 0 && (
              <span className="ml-2">
                <span className="text-purple-400 font-semibold">{sandboxAgents.length}</span> sandbox
              </span>
            )}
          </div>
          {onSpawn && (
            <button
              onClick={onSpawn}
              className="px-4 py-1.5 text-sm bg-purple-600 hover:bg-purple-500 text-white rounded-lg transition-colors flex items-center gap-1"
            >
              <span>+</span> Spawn
            </button>
          )}
        </div>
      </div>

      {/* Agent List */}
      <div className="space-y-3">
        {agents.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            No agents yet. Spawn your first Clawbot to get started.
          </div>
        ) : (
          agents.map((agent) => (
            <AgentCard
              key={agent.did}
              agent={agent}
              onSelect={onSelect}
              onPause={onPause}
              onTerminate={onTerminate}
              onUpgrade={onUpgrade}
              onViewLogs={onViewLogs}
            />
          ))
        )}
      </div>
    </div>
  );
}

interface AgentCardProps {
  agent: Agent;
  onSelect?: (agent: Agent) => void;
  onPause?: (agent: Agent) => void;
  onTerminate?: (agent: Agent) => void;
  onUpgrade?: (agent: Agent) => void;
  onViewLogs?: (agent: Agent) => void;
}

function AgentCard({
  agent,
  onSelect,
  onPause,
  onTerminate,
  onUpgrade,
  onViewLogs,
}: AgentCardProps) {
  const statusColor = StatusColors[agent.status];
  const isActive = agent.status === 'active';
  const isSandbox = agent.status === 'sandbox';

  return (
    <div
      className={`bg-gray-800/50 border border-gray-700 rounded-lg p-4 hover:border-gray-600 transition-colors ${
        onSelect ? 'cursor-pointer' : ''
      }`}
      onClick={() => onSelect?.(agent)}
    >
      {/* Top Row: DID, Name, Status, Tier, Level */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-3">
          <code className="text-sm text-gray-400 font-mono">{formatDid(agent.did, 6)}</code>
          <span className="text-white font-medium">{agent.name}</span>
        </div>
        <div className="flex items-center gap-2">
          <StatusBadge status={agent.status} color={statusColor} />
          <TierBadge tier={agent.tier} />
          <LevelBadge level={agent.level} />
        </div>
      </div>

      {/* Stats Row */}
      <div className="flex items-center gap-6 text-sm mb-3">
        <div>
          <span className="text-gray-500">CPU:</span>{' '}
          <span className="text-gray-300">{formatPercent(agent.cpu)}</span>
        </div>
        <div>
          <span className="text-gray-500">RAM:</span>{' '}
          <span className="text-gray-300">{formatRam(agent.ramMb)}</span>
        </div>
        <div>
          <span className="text-gray-500">Uptime:</span>{' '}
          <span className="text-gray-300">{formatUptime(agent.uptime)}</span>
        </div>
        <div>
          <span className="text-gray-500">Tasks:</span>{' '}
          <span className="text-gray-300">{agent.tasksCompleted.toLocaleString()}</span>
        </div>
      </div>

      {/* Actions Row */}
      <div className="flex items-center gap-2">
        {onViewLogs && (
          <ActionButton onClick={() => onViewLogs(agent)}>View Logs</ActionButton>
        )}
        {isActive && onPause && (
          <ActionButton onClick={() => onPause(agent)}>Pause</ActionButton>
        )}
        {isActive && onTerminate && (
          <ActionButton onClick={() => onTerminate(agent)} variant="danger">
            Terminate
          </ActionButton>
        )}
        {isSandbox && onUpgrade && (
          <ActionButton onClick={() => onUpgrade(agent)} variant="primary">
            Upgrade to Production
          </ActionButton>
        )}
      </div>
    </div>
  );
}

function StatusBadge({ status, color }: { status: AgentStatus; color: string }) {
  const isActive = status === 'active';
  return (
    <span
      className="flex items-center gap-1.5 px-2 py-0.5 text-xs rounded-full"
      style={{
        backgroundColor: `${color}20`,
        color: color,
      }}
    >
      <span
        className={`w-1.5 h-1.5 rounded-full ${isActive ? 'animate-pulse' : ''}`}
        style={{ backgroundColor: color }}
      />
      {status.toUpperCase()}
    </span>
  );
}

function TierBadge({ tier }: { tier: Tier }) {
  const colors: Record<Tier, string> = {
    0: 'bg-gray-700 text-gray-300',
    1: 'bg-blue-900/50 text-blue-300',
    2: 'bg-purple-900/50 text-purple-300',
    3: 'bg-yellow-900/50 text-yellow-300',
  };

  return (
    <span className={`px-2 py-0.5 text-xs rounded ${colors[tier]}`}>
      T{tier}
    </span>
  );
}

function LevelBadge({ level }: { level: number }) {
  return (
    <span className="px-2 py-0.5 text-xs bg-gray-700 text-gray-300 rounded">
      L{level}
    </span>
  );
}

interface ActionButtonProps {
  children: React.ReactNode;
  onClick: () => void;
  variant?: 'default' | 'primary' | 'danger';
}

function ActionButton({ children, onClick, variant = 'default' }: ActionButtonProps) {
  const variants = {
    default: 'text-gray-400 hover:text-white hover:bg-gray-700',
    primary: 'text-purple-400 hover:text-purple-300 hover:bg-purple-900/50',
    danger: 'text-red-400 hover:text-red-300 hover:bg-red-900/50',
  };

  return (
    <button
      onClick={(e) => {
        e.stopPropagation();
        onClick();
      }}
      className={`px-3 py-1 text-xs rounded transition-colors ${variants[variant]}`}
    >
      {children}
    </button>
  );
}
